import { Auth0Client } from '@auth0/auth0-spa-js';
import Axios, { Method, AxiosError } from 'axios';
import {
	ritchieIdApiUrl,
	clientId,
	auth0Domain,
	redirectUri,
	scopes
} from '../../config';
import { Translations } from '../translations/types';

class API {
	private auth0 = new Auth0Client({
		client_id: clientId,
		domain: auth0Domain,
		audience: ritchieIdApiUrl,
		redirect_uri: redirectUri,
		scope: scopes,
		cacheLocation: 'localstorage'
	});

	async getTranslations(language = '') {
		let url = './translation.json';

		if (language) url += `?language=${language}`

		// Note that this request gets intercepted and modified by the Edge Lambda (in this repo)
		// to serve the proper language
		const { data } = await Axios.get(url);

		return data;
	}

	async getIdentities() {
		try {
			return await this.sendRequest('get', `v1/web/identities`);
		}
		catch(error: any) {
			this.handleLinkingServiceError(error);
		}
	}

	private handleLinkingServiceError = (error: AxiosError) => {
		const {
			response = {},
			response: {
				data = response
			} = {}
		} = error;

		throw data;
	}

	async linkIdentity(systemId: string, credentials: { loginName: string, password: string }) {
		try {
			return await this.sendRequest('put', `v1/web/identities/${systemId}`, credentials);
		}
		catch(error: any) {
			this.handleLinkingServiceError(error);
		}
	}

	async unlinkIdentity(systemId: string, localId: string) {
		try {
			return await this.sendRequest('delete', `v1/web/identities/${systemId}`, { localId });
		}
		catch(error: any) {
			this.handleLinkingServiceError(error);
		}
	}

	private sendRequest = async (method: Method, path: string, data?: any) => {
		const token = await this.auth0.getTokenSilently();

		const response = await Axios.request({
			method,
			url: `${ritchieIdApiUrl}${path}`,
			data,
			headers: {
				'Authorization': `Bearer ${token}`,
				'Content-Type': 'application/json'
			}
		});

		return response.data;
	}
}

export type ApiError = {
	errorType: keyof Translations['apiErrors'];
}

export function mapApiError(error: ApiError, translations: Translations['apiErrors']) {
	const { errorType = 'internal_server_error' } = error || {};

	return translations[errorType] || translations['internal_server_error'];
}

export const api = new API();